import { useState } from "react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Loader2, ArrowRight, Phone } from "lucide-react";
import { BRAND } from "@/data/content";

const INTERESTS = [
  "Foundation Package — $299/mo",
  "Transformation Package — $449/mo",
  "Peak Performance Package — $699/mo",
  "NextGen Pro Peptides Inquiry",
  "General Question",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: INTERESTS[1],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      trackEvent("generate_lead", {
        source: "contact_form",
        interest: form.interest,
      });
      toast.success("Application received. Coach J will reach out within 24h.");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Submission failed. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-ng-bg py-24 md:py-36 border-t border-ng-line hex-bg-strong"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-5">07 · Apply</div>
            <h2 className="font-display font-bold uppercase text-4xl md:text-5xl tracking-tight text-white leading-[1]">
              Ready to <br />
              <span className="text-ng-blue italic">transform?</span>
            </h2>
            <p className="mt-6 text-white/65 font-light leading-relaxed">
              Tell Coach J about your current state, goals, and what&rsquo;s
              holding you back. Every application is reviewed personally with
              a response within 24 hours.
            </p>

            <a
              href={`tel:${BRAND.phone_link}`}
              className="mt-8 inline-flex items-center gap-3 border border-ng-blue/40 px-5 py-4 bg-ng-surface hover:border-ng-blue hover:bg-ng-blue/5 transition-colors group"
              data-testid="contact-phone-cta"
            >
              <Phone size={18} strokeWidth={1.5} className="text-ng-blue" />
              <div>
                <div className="label-eyebrow">Or Text or Call Coach J</div>
                <div className="font-display font-bold text-2xl text-white group-hover:text-ng-blue transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>

            <div className="mt-8 space-y-3 text-white/60 text-sm">
              <Bullet text="Reviewed by Coach J personally" />
              <Bullet text="Free 30-minute strategy call" />
              <Bullet text="100% confidential — your goals are yours" />
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="border border-ng-blue/40 bg-ng-surface p-10 shadow-[0_0_60px_-20px_rgba(30,144,255,0.5)]"
              >
                <div className="label-eyebrow mb-3">Received</div>
                <h3 className="font-display font-bold uppercase text-3xl text-white">
                  Your application is in.
                </h3>
                <p className="mt-4 text-white/70 font-light leading-relaxed">
                  Coach J will review your goals and reach out within 24 hours
                  from{" "}
                  <span className="text-ng-blue">{BRAND.email}</span> or{" "}
                  <span className="text-ng-blue">{BRAND.phone}</span>.
                </p>
                <button
                  onClick={() => {
                    setDone(false);
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      interest: INTERESTS[1],
                      message: "",
                    });
                  }}
                  className="mt-8 text-xs uppercase tracking-[0.22em] text-ng-blue font-display font-bold border-b border-ng-blue/40 pb-1 hover:border-ng-blue transition-colors"
                  data-testid="contact-reset"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="space-y-7 border border-ng-line bg-ng-surface/50 p-8 md:p-10"
              >
                <Field
                  label="Full Name"
                  testId="contact-name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Email"
                    type="email"
                    testId="contact-email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    required
                  />
                  <Field
                    label="Phone (optional)"
                    testId="contact-phone"
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                  />
                </div>

                <div>
                  <label className="label-eyebrow block mb-3">
                    Interested in
                  </label>
                  <select
                    data-testid="contact-interest"
                    value={form.interest}
                    onChange={(e) => update("interest", e.target.value)}
                    className="w-full bg-transparent border-b border-ng-line text-white py-4 focus:outline-none focus:border-ng-blue transition-colors font-display"
                  >
                    {INTERESTS.map((i) => (
                      <option key={i} value={i} className="bg-ng-surface">
                        {i}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label-eyebrow block mb-3">
                    Tell us about your goals
                  </label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={4}
                    placeholder="Current training, nutrition, sleep, target outcomes..."
                    className="w-full bg-transparent border-b border-ng-line text-white py-4 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 bg-ng-blue text-white px-8 py-4 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors disabled:opacity-60 shadow-[0_0_40px_-8px_rgba(30,144,255,0.7)]"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
                <p className="text-white/40 text-xs">
                  By submitting, you agree to be contacted by NextGen Performance via email or phone.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-6 h-px bg-ng-blue" />
      <span>{text}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="label-eyebrow block mb-3">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border-b border-ng-line text-white py-4 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30"
      />
    </div>
  );
}
